import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SearchBar from "@/components/SearchBar";
import ListingCard from "@/components/ListingCard";
import FounderPromo from "@/components/FounderPromo";
import { Waves, ShieldCheck, KeyRound, ArrowRight, Award } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1603473036383-df544a1dcada?w=2400&q=85";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/docks/featured").then((r) => setFeatured(r.data.docks)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden">
        <img src={HERO} alt="Lake dock at dawn" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/60" />
        <div className="relative container-editorial pt-24 pb-40 text-white">
          <div className="max-w-2xl">
            <div
              data-testid="veteran-badge-hero"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/25 px-3.5 py-1.5 text-xs uppercase tracking-[0.15em] text-white/95 mb-5"
            >
              <Award className="h-3.5 w-3.5" strokeWidth={1.75} />
              <span>Veteran-Owned · Iraq War Veteran</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              Rent a slip.<br/>
              <span className="italic text-[#F1C6B5]">Keep your boat</span><br/>
              or jet ski on the water.
            </h1>
            <p className="mt-6 text-base sm:text-lg max-w-lg text-white/85">
              Owners rent their unused slips and boathouses — lake, river, bay, or coast — by the day, week, month, or year.
              Boaters and PWC riders skip the marina waitlist. A calmer way to live on the water.
            </p>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-[-40px] container-editorial">
          <SearchBar variant="hero" />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-editorial mt-32">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.3em] text-accent mb-4">How SlipShare Works</div>
            <h2 className="font-display text-4xl leading-tight">Three quiet steps between you and the water.</h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-6">
            {[
              { icon: Waves, k: "Find your lake", d: "Browse verified docks across your favorite lakes — filter by length, lift, and amenities." },
              { icon: KeyRound, k: "Book your dates", d: "Pick daily, weekly, monthly, or yearly. Owners approve; we handle the payment." },
              { icon: ShieldCheck, k: "Dock and go", d: "Coordinate access with your owner and enjoy the season. Rent longer any time." },
            ].map(({icon: Icon, k, d}, i) => (
              <div key={k} data-testid={`step-${i}`} className="rounded-xl border border-border bg-card p-6">
                <Icon className="h-5 w-5 text-accent" strokeWidth={1.5}/>
                <div className="mt-6 font-display text-xl">{k}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER PROMO */}
      <FounderPromo />

      {/* FEATURED */}
      <section className="container-editorial mt-32">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-accent mb-3">Featured Slips</div>
            <h2 className="font-display text-4xl">Hand-picked docks, ready this season.</h2>
          </div>
          <Link to="/search" data-testid="see-all-docks" className="hidden sm:inline-flex items-center gap-2 text-sm link-underline">
            See all docks <ArrowRight className="h-4 w-4" strokeWidth={1.5}/>
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {featured.map((d) => <ListingCard key={d.dock_id} dock={d} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="container-editorial mt-32">
        <div className="relative overflow-hidden rounded-2xl grain">
          <img src="https://images.unsplash.com/photo-1769913995907-e1cc7ff527a1?w=2000&q=85" alt="Lake vista" className="absolute inset-0 h-full w-full object-cover"/>
          <div className="absolute inset-0 bg-primary/70"/>
          <div className="relative px-8 py-20 sm:px-16 text-primary-foreground max-w-2xl">
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">Have a dock sitting empty?</h2>
            <p className="mt-4 text-white/85">List your slip in minutes. Offset the cost of lakefront living, meet careful boaters, keep your dock in use.</p>
            <Link to="/list" data-testid="cta-list-btn" className="inline-block mt-8 rounded-full bg-accent px-6 py-3 text-sm font-medium hover:bg-accent/90 transition-colors">
              List your dock
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
